/**
 * Hooks for the admin session-moderation API. Every one of these 403s for a
 * non-admin server-side; the queries are additionally gated on ``enabled`` so a
 * non-admin never fires them at all.
 *
 * Discard and restore both invalidate the opinions list as well as the session
 * list: the cascade queues a Phase C recompute for every affected topic, so the
 * team positions on ``/opinions`` are stale the moment one lands.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi, type ApiError } from "@/lib/api";
import type {
  AccessAssignment,
  AccessPerson,
  AccessRole,
  AccessState,
  AdminSession,
  BugReport,
  BugTriage,
  DiscardResult,
  LauncherCosts,
  LlmCosts,
  Persona,
  SynthesisQueue,
  ToolCall,
  UsageReport,
} from "@/lib/types";

const sessionsKey = ["admin-sessions"] as const;

export function useAdminSessions(enabled: boolean, includeDiscarded: boolean) {
  const apiCall = useApi();
  return useQuery<AdminSession[], ApiError>({
    queryKey: [...sessionsKey, includeDiscarded],
    queryFn: ({ signal }) =>
      apiCall<AdminSession[]>("/api/admin/sessions", {
        query: { include_discarded: includeDiscarded },
        signal,
      }),
    enabled,
  });
}

/** The LLM-call ledger, priced (V009). Admin-only like everything here. */
export function useLlmCosts(enabled: boolean) {
  const apiCall = useApi();
  return useQuery<LlmCosts, ApiError>({
    queryKey: ["admin-llm-costs"],
    queryFn: ({ signal }) => apiCall<LlmCosts>("/api/admin/llm-costs", { signal }),
    enabled,
  });
}

export function useLauncherCosts(days = 30) {
  const apiCall = useApi();
  return useQuery<LauncherCosts, ApiError>({
    queryKey: ["admin-launcher-costs", days],
    queryFn: ({ signal }) => apiCall<LauncherCosts>("/api/admin/launcher-costs", { query: { days }, signal }),
  });
}

function useSessionMutation(path: (id: string) => string) {
  const apiCall = useApi();
  const qc = useQueryClient();
  return useMutation<DiscardResult, ApiError, string>({
    mutationFn: (id) => apiCall<DiscardResult>(path(id), { method: "POST" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: sessionsKey });
      void qc.invalidateQueries({ queryKey: ["opinions"] });
      // The discarded respondent's own view changes too (their topic reverts to
      // not-started) — refresh it in case the admin is looking at their own.
      void qc.invalidateQueries({ queryKey: ["preference-topics"] });
    },
  });
}

/** Discard one session, cascading to whatever its interview seeded elsewhere. */
export function useDiscardSession() {
  return useSessionMutation((id) => `/api/admin/sessions/${id}/discard`);
}

/** Undo a discard, replaying exactly the routed signals it stripped. */
export function useRestoreSession() {
  return useSessionMutation((id) => `/api/admin/sessions/${id}/restore`);
}

/** Discard every live session for one respondent (takes the Entra oid). */
export function useDiscardRespondent() {
  return useSessionMutation((oid) => `/api/admin/respondents/${oid}/discard`);
}

/**
 * Take a session out of the team aggregate, or put it back.
 *
 * Unlike discard, this changes nothing the respondent can see — so the
 * respondent-facing caches are deliberately NOT invalidated here. Only the
 * admin list and the positions it feeds move.
 */
export function useSetSessionExcluded() {
  const apiCall = useApi();
  const qc = useQueryClient();
  return useMutation<AdminSession, ApiError, { responseId: string; excluded: boolean; note?: string }>({
    mutationFn: ({ responseId, excluded, note }) =>
      apiCall<AdminSession>(`/api/admin/sessions/${responseId}/excluded`, {
        method: "POST",
        body: { excluded, note: note ?? null },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: sessionsKey });
      void qc.invalidateQueries({ queryKey: ["opinions"] });
    },
  });
}

const synthesisQueueKey = ["admin-synthesis-queue"] as const;

/**
 * Past synthesis passes plus the scheduler's current intent.
 *
 * Polled, because a running pass finishes on its own and a debounce fires
 * without any client action — a static view would show a queue that quietly
 * stopped being true.
 */
export function useSynthesisQueue(enabled: boolean) {
  const apiCall = useApi();
  return useQuery<SynthesisQueue, ApiError>({
    queryKey: synthesisQueueKey,
    enabled,
    refetchInterval: 15_000,
    queryFn: ({ signal }) => apiCall<SynthesisQueue>("/api/admin/synthesis/queue", { signal }),
  });
}

const accessKey = ["admin-access"] as const;

/** Who can sign in: the app role assignments on the Northstar SP, via Graph. */
export function useAccess(enabled: boolean) {
  const apiCall = useApi();
  return useQuery<AccessState, ApiError>({
    queryKey: accessKey,
    queryFn: ({ signal }) => apiCall<AccessState>("/api/admin/access", { signal }),
    enabled,
  });
}

/** Tenant users matching a name or address; the query is off until two characters. */
export function useAccessPeople(q: string) {
  const apiCall = useApi();
  const query = q.trim();
  return useQuery<AccessPerson[], ApiError>({
    queryKey: ["admin-access-people", query],
    queryFn: ({ signal }) => apiCall<AccessPerson[]>("/api/admin/access/people", { query: { q: query }, signal }),
    enabled: query.length >= 2,
    staleTime: 30_000,
  });
}

export function useGrantAccess() {
  const apiCall = useApi();
  const qc = useQueryClient();
  return useMutation<AccessAssignment, ApiError, { principalId: string; role: AccessRole }>({
    mutationFn: ({ principalId, role }) =>
      apiCall<AccessAssignment>("/api/admin/access/grants", {
        method: "POST",
        body: { principal_id: principalId, role },
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: accessKey }),
  });
}

export function useRevokeAccess() {
  const apiCall = useApi();
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (assignmentId) => apiCall<void>(`/api/admin/access/grants/${assignmentId}`, { method: "DELETE" }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: accessKey }),
  });
}

const personasKey = ["admin-personas"] as const;

/** The persona registry: synthetic voices that sit the survey without voting. */
export function usePersonas(enabled = true) {
  const apiCall = useApi();
  return useQuery<Persona[], ApiError>({
    queryKey: personasKey,
    queryFn: ({ signal }) => apiCall<Persona[]>("/api/admin/personas", { signal }),
    enabled,
  });
}

export interface PersonaInput {
  persona_key: string;
  display_name: string;
  description: string | null;
  accountable_upn: string;
  operator_oid: string;
  operator_upn: string | null;
}

export function useCreatePersona() {
  const apiCall = useApi();
  const qc = useQueryClient();
  return useMutation<Persona, ApiError, PersonaInput>({
    mutationFn: (body) => apiCall<Persona>("/api/admin/personas", { method: "POST", body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: personasKey }),
  });
}

/** Retire (it can no longer answer; what it filed stays, marked) or restore. */
export function useSetPersonaRetired() {
  const apiCall = useApi();
  const qc = useQueryClient();
  return useMutation<Persona, ApiError, { key: string; retired: boolean }>({
    mutationFn: ({ key, retired }) =>
      apiCall<Persona>(`/api/admin/personas/${key}/${retired ? "retire" : "restore"}`, { method: "POST" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: personasKey });
      void qc.invalidateQueries({ queryKey: ["opinions"] });
    },
  });
}

/** Who is using what: the MCP tool-call and HTTP request ledgers (V016). */
export function useUsage(days = 30) {
  const apiCall = useApi();
  return useQuery<UsageReport, ApiError>({
    queryKey: ["admin-usage", days],
    queryFn: ({ signal }) => apiCall<UsageReport>("/api/admin/usage", { query: { days }, signal }),
  });
}

/** The most recent tool calls, each with its arguments and result. */
export function useRecentToolCalls(enabled: boolean, filter: { tool?: string; userUpn?: string } = {}) {
  const apiCall = useApi();
  return useQuery<ToolCall[], ApiError>({
    queryKey: ["admin-usage-calls", filter.tool ?? "", filter.userUpn ?? ""],
    queryFn: ({ signal }) =>
      apiCall<ToolCall[]>("/api/admin/usage/calls", {
        query: { limit: 100, tool: filter.tool, user_upn: filter.userUpn },
        signal,
      }),
    enabled,
  });
}

const bugReportsKey = ["admin-bug-reports"] as const;

/** Problems filed through `survey_report_bug`: open and triaged unless `includeClosed`. */
export function useBugReports(includeClosed: boolean) {
  const apiCall = useApi();
  return useQuery<BugReport[], ApiError>({
    queryKey: [...bugReportsKey, includeClosed],
    queryFn: ({ signal }) =>
      apiCall<BugReport[]>("/api/admin/bug-reports", { query: { include_closed: includeClosed }, signal }),
  });
}

export function useTriageBugReport() {
  const apiCall = useApi();
  const qc = useQueryClient();
  return useMutation<BugReport, ApiError, { reportId: string; changes: BugTriage }>({
    mutationFn: ({ reportId, changes }) =>
      apiCall<BugReport>(`/api/admin/bug-reports/${reportId}`, { method: "POST", body: changes }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: bugReportsKey }),
  });
}
